import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, AlertCircle, RefreshCw, Calendar, TrendingUp } from 'lucide-react';
import axios from 'axios';

const UserActivityChart = () => {
  const [analytics, setAnalytics] = useState({
    loading: true,
    error: null,
    weeklyData: [],
    monthlyData: []
  });

  const [dateFilter, setDateFilter] = useState({
    preset: 'last_3_months',
    period: 'weekly',
    startDate: '',
    endDate: '',
    showCustom: false
  });

  // Date preset options
  const datePresets = [
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Color palette for different users
  const COLORS = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#F97316', // Orange
    '#84CC16', // Lime
    '#EC4899', // Pink
    '#6366F1'  // Indigo
  ];

  useEffect(() => {
    loadUserActivityData();
  }, [dateFilter.preset, dateFilter.period, dateFilter.startDate, dateFilter.endDate]);

  const loadUserActivityData = async () => {
    setAnalytics(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const dateParams = getDateParams();
      
      const [weeklyResponse, monthlyResponse] = await Promise.all([
        axios.get('/api/submissions/analytics/user-activity-weekly', { params: dateParams }),
        axios.get('/api/submissions/analytics/user-activity-monthly', { params: dateParams })
      ]);
      
      // Transform data for multi-user line chart
      const weeklyData = formatUserActivityData(weeklyResponse.data.data, 'weekly');
      const monthlyData = formatUserActivityData(monthlyResponse.data.data, 'monthly');
      
      setAnalytics({
        loading: false,
        error: null,
        weeklyData,
        monthlyData
      });
      
    } catch (error) {
      console.error('User activity analytics loading error:', error);
      setAnalytics(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Failed to load user activity analytics'
      }));
    }
  };

  const getDateParams = () => {
    const now = new Date();
    let params = {};
    
    switch (dateFilter.preset) {
      case 'last_3_months':
        params.startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
        break;
      case 'last_6_months':
        params.startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
        break;
      case 'last_year':
        params.startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
        break;
      case 'custom':
        if (dateFilter.startDate && dateFilter.endDate) {
          params.startDate = dateFilter.startDate;
          params.endDate = dateFilter.endDate;
        }
        break;
    }
    
    return params;
  };

  const formatUserActivityData = (data, period) => {
    // Group data by period and create a structure suitable for multi-line chart
    const periodMap = new Map();
    const userSet = new Set();
    
    // Collect all periods and users
    data.forEach(item => {
      const periodKey = item.period;
      const label = period === 'weekly' ? formatWeekLabel(item.period) : formatMonthLabel(item.period);
      
      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, {
          period: periodKey,
          label: label,
          startDate: item.startDate
        });
      }
      
      userSet.add(item.userName);
      periodMap.get(periodKey)[item.userName] = item.count;
    });
    
    // Convert to array and ensure all users have values for all periods
    const result = Array.from(periodMap.values()).map(periodData => {
      userSet.forEach(userName => {
        if (!(userName in periodData)) {
          periodData[userName] = 0;
        }
      });
      return periodData;
    });
    
    // Sort by period
    return result.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  };

  const formatWeekLabel = (period) => {
    const [year, week] = period.split('-W');
    return `Week ${parseInt(week)}, ${year}`;
  };

  const formatMonthLabel = (period) => {
    const [year, month] = period.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const handlePresetChange = (preset) => {
    setDateFilter(prev => ({
      ...prev,
      preset,
      showCustom: preset === 'custom'
    }));
  };

  const handlePeriodChange = (period) => {
    setDateFilter(prev => ({
      ...prev,
      period
    }));
  };

  const handleCustomDateChange = (field, value) => {
    setDateFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getCurrentData = () => {
    return dateFilter.period === 'weekly' ? analytics.weeklyData : analytics.monthlyData;
  };

  const getUniqueUsers = () => {
    const data = getCurrentData();
    if (data.length === 0) return [];
    
    const users = new Set();
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'period' && key !== 'label' && key !== 'startDate') {
          users.add(key);
        }
      });
    });
    
    return Array.from(users).sort();
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm text-gray-600 mb-1">{label}</p>
          {payload.map((item, index) => (
            <p key={index} className="text-sm font-semibold" style={{ color: item.color }}>
              {`${item.dataKey}: ${item.value} assignments`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (analytics.loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading user activity...</p>
          </div>
        </div>
      </div>
    );
  }

  const currentData = getCurrentData();
  const uniqueUsers = getUniqueUsers();

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Activity className="h-5 w-5 text-green-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">User Activity Over Time</h3>
        </div>
        <button
          onClick={loadUserActivityData}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Controls */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Period
            </label>
            <select
              value={dateFilter.preset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="mt-1 block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {datePresets.map(preset => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              View
            </label>
            <div className="flex border border-gray-300 rounded-md">
              <button
                onClick={() => handlePeriodChange('weekly')}
                className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                  dateFilter.period === 'weekly'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => handlePeriodChange('monthly')}
                className={`px-3 py-2 text-sm font-medium rounded-r-md border-l ${
                  dateFilter.period === 'monthly'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          {dateFilter.showCustom && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                  className="mt-1 block w-36 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                  className="mt-1 block w-36 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {analytics.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{analytics.error}</p>
              <button
                onClick={loadUserActivityData}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {currentData.length === 0 || uniqueUsers.length === 0 ? (
        <div className="text-center py-12">
          <Activity className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No user activity data</h3>
          <p className="mt-1 text-sm text-gray-500">
            No assignment activity found for the selected period.
          </p>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={currentData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {uniqueUsers.map((userName, index) => (
                  <Line
                    key={userName}
                    type="monotone"
                    dataKey={userName}
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={2}
                    dot={{ fill: COLORS[index % COLORS.length], strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: COLORS[index % COLORS.length], strokeWidth: 2 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Summary */}
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center">
                  <TrendingUp className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-blue-800">Active Users</p>
                    <p className="text-2xl font-bold text-blue-900">{uniqueUsers.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center">
                  <Calendar className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-green-800">Total Assignments</p>
                    <p className="text-2xl font-bold text-green-900">
                      {currentData.reduce((total, period) => {
                        return total + uniqueUsers.reduce((sum, user) => sum + (period[user] || 0), 0);
                      }, 0)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center">
                  <Activity className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-purple-800">Avg per Period</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {currentData.length > 0 
                        ? Math.round(currentData.reduce((total, period) => {
                            return total + uniqueUsers.reduce((sum, user) => sum + (period[user] || 0), 0);
                          }, 0) / currentData.length * 10) / 10
                        : 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserActivityChart;
